import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ComponentRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginService } from '@ebw-monorepo/login';
import {
  ConfigSidebarService,
  DrawingService,
  FeatureSelectService,
  IMapConfig,
  LayerStylesService,
  MapComponent,
  MapService,
  MapTableService,
  REPORT_DIALOG_CUSTOM_STYLE,
  ReportDialogComponent
} from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { NavigationService } from '@ebw-monorepo/navigation';
import { PERMISSION_NAME, PermissionService, SplitViewService } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { CustomButtonComponent } from 'libs/ng-components/map/src/lib/components/custom-button/custom-button.component';
import { RaccordiCavoEditComponent } from 'libs/ng-components/map/src/lib/components/sidebar/fiber-joints/raccordi-cavo-edit/raccordi-cavo-edit.component';
import { IMapTable, MapTableType } from 'libs/ng-components/map/src/lib/interfaces/mapTable.interface';
import { FeatureActionEvent } from 'libs/ng-components/map/src/lib/models/feature-action-event';
import { FeatureViewerService } from 'libs/ng-components/map/src/lib/services/feature-viewer.service';
import { TelcoService } from 'libs/ng-components/map/src/lib/services/telco.service';
import { SplitViewLayoutConf } from 'libs/ng-components/utils/src/lib/models/split-view-conf';
import { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import { Geometry, Point } from 'ol/geom';
import { Subject, Subscription, debounceTime, filter, lastValueFrom, take, takeUntil } from 'rxjs';
import { IToolbarAction } from '../../model/toolbar-action';
import { ConfigService } from '../../services/config.service';
import { ToolbarService } from '../../services/toolbar.service';

@Component({
  selector: 'ebw-monorepo-map-wrapper',
  templateUrl: './map-wrapper.component.html',
  styleUrls: ['./map-wrapper.component.scss'],
  animations: [
    trigger('bubbleGrow', [
      transition(':enter', [
        animate('150ms ease-in', keyframes([style({ transform: 'scale(0)', opacity: 0 }), style({ transform: 'scale(1)', opacity: 1 })]))
      ]),
      transition(':leave', [
        animate('150ms ease-out', keyframes([style({ transform: 'scale(1)', opacity: 1 }), style({ transform: 'scale(0)', opacity: 0 })]))
      ])
    ])
  ]
})
export class MapWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapview') mapview: MapComponent;

  dynamicConfigMappaJSON: any;
  mapConfig: IMapConfig;

  public splitLayoutConf: SplitViewLayoutConf = {
    isEnable: false,
    halfScreen: true,
    collapsed: false
  };

  private _subscriptions: Subscription[] = [];
  private _menuSubscription: Subscription;
  private _lat;
  private _lon;
  private _zoom;
  private _highlightStyle;

  private destroy$ = new Subject<void>();

  MapTableTypeEnum = MapTableType;
  mapTableConfig: IMapTable;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _mapService: MapService,
    private _layerStylesService: LayerStylesService,
    private _drawingService: DrawingService,
    private _navigation: NavigationService,
    private _configService: ConfigService,
    private _featureSelectService: FeatureSelectService,
    private _toolbarService: ToolbarService,
    private _translateService: TranslateService,
    private _ebwModal: EbwModalService,
    private _mapTableSrv: MapTableService,
    private _configSidebarService: ConfigSidebarService,
    private _featureViewerService: FeatureViewerService,
    private _splitViewService: SplitViewService,
    private _telcoService: TelcoService,
    private matIconRegistry: MatIconRegistry,
    private _domSanitizer: DomSanitizer,
    private _permissionService: PermissionService,
    private _dialog: MatDialog
  ) {}

  isSidebarActionActive(): boolean {
    try {
      const urlTree = this._router.parseUrl(this._router.url);
      return !!urlTree.root?.children?.['primary']?.children?.['sidebarAction'];
    } catch {
      return false;
    }
  }

  ngOnInit(): void {
    const { sidebarActionIds } = this._configService.getConfigGlobale();

    this._initSubscriptions();
    this._initFeatureModal();
    this.handleMapTableView();

    // ---------------------------------------------------------------------------------
    // TELCO
    this._setTelcoConfig();

    // ---------------------------------------------------------------------------------

    this._menuSubscription = this._toolbarService.actionEmitter$.pipe(debounceTime(100)).subscribe(async (action) => {
      const navigationExtras: any = {};

      if (!this.isSidebarActionActive()) {
        navigationExtras.relativeTo = this._route;
      } else {
        navigationExtras.relativeTo = this._route.parent;
      }

      switch (action.id) {
        case sidebarActionIds.layerConfigurator:
          await this._router.navigate([{ outlets: { sidebarAction: ['layers'] } }], {
            ...navigationExtras,
            state: { config: this.mapConfig.sceltaRappresentazione }
          });
          action.cb(action);
          break;

        case sidebarActionIds.dynamicLayers:
          await this._router.navigate([{ outlets: { sidebarAction: ['dynamic-layers'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;

        case sidebarActionIds.drawingTool:
          this.mapview.openDrawingTool();
          action.cb(action);
          break;

        case sidebarActionIds.shapeConfigurator:
          await this._router.navigate([{ outlets: { sidebarAction: ['shape-layers'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;

        case sidebarActionIds.bookmarks:
          await this._router.navigate([{ outlets: { sidebarAction: ['bookmarks'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;

        case sidebarActionIds.query:
          await this._router.navigate([{ outlets: { sidebarAction: ['query'] } }], {
            ...navigationExtras,
            state: { config: this.mapConfig.datasets }
          });
          action.cb(action);
          break;

        case sidebarActionIds.valve: {
          const navigated = await this._router.navigate([{ outlets: { sidebarAction: ['valves'] } }], {
            ...navigationExtras
          });
          // false = navigazione bloccata dal guard (c'è già uno strumento aperto): il pannello non va aperto
          if (navigated !== false) {
            action.cb(action);
          }
          break;
        }

        case sidebarActionIds.otdr:
          await this._router.navigate([{ outlets: { sidebarAction: ['otdr'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;

        case sidebarActionIds.psa:
          await this._router.navigate([{ outlets: { sidebarAction: ['psa'] } }], {
            ...navigationExtras,
            state: { config: this.mapConfig.sceltaRappresentazione }
          });
          action.cb(action);
          break;

        case sidebarActionIds.controlli:
          await this._router.navigate([{ outlets: { sidebarAction: ['controlli'] } }], navigationExtras);
          action.cb(action);
          break;

        case sidebarActionIds.trasmissioneAntea:
          await this._router.navigate([{ outlets: { sidebarAction: ['trasmissione-antea'] } }], navigationExtras);
          action.cb(action);
          break;

        case sidebarActionIds.checkInOut:
          await this._router.navigate([{ outlets: { sidebarAction: ['checkin-out'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;

        case sidebarActionIds.analisiSchiacciamenti:
          await this._router.navigate([{ outlets: { sidebarAction: ['analisi-schiacciamenti'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;
        case sidebarActionIds.tracing:
          this._router.navigate([{ outlets: { sidebarAction: ['tracing'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;

        case sidebarActionIds.checkInAnalysis:
          this._router.navigate([{ outlets: { sidebarAction: ['checkin-analysis'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;
        case sidebarActionIds.fiberJoints:
          this._router.navigate([{ outlets: { sidebarAction: ['fiber-joints'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;
        case sidebarActionIds.toolFattibilita:
          this._router.navigate([{ outlets: { sidebarAction: ['fattibilita'] } }], {
            ...navigationExtras
          });
          action.cb(action);
          break;
      }
    });

    this._configService.mapConfiguration$.pipe(debounceTime(100)).subscribe((conf) => {
      if (conf) {
        this._setMapConfigAndToolbar(conf);
      }
    });

    // listen to the allConfigurationReloadRequest$ Subject to reload all the configurations from the database
    this._mapService.allConfigurationReloadRequest$.pipe(debounceTime(100)).subscribe(() => {
      // get all the configurations and listen to its response
      this._configService.getAllConfigurations().subscribe({
        next: () => {
          //
        },
        // display an error message in case there was an error retrieving the configurations
        error: () => {
          const modalContent = this._translateService.instant('common.data_retrieving_error');
          this._ebwModal.openErrorModal({
            content: modalContent,
            title: this._translateService.instant('common.error')
          });
        }
      });
    });

    this._mapService.mapConfigurationReloadRequest$.pipe(debounceTime(200)).subscribe((res) => {
      this._configService.getMapConfigurationFromApi().subscribe({
        next: (conf) => (this._highlightStyle = this._layerStylesService.buildStyleFromFeatureStyle(this._drawingService.highlightFeatureStyle())),
        error: (err) =>
          this._ebwModal.openErrorModal({
            content: this._translateService.instant('common.data_retrieving_error'),
            title: this._translateService.instant('common.error')
          })
      });
    });

    this._featureViewerService.nodeAction$.subscribe((event) => {
      this.changeActivePreview(event);
    });

    this._splitViewService.viewLayountConfig$.subscribe((event) => {
      if (!event) {
        return;
      }

      this.splitLayoutConf = event;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this._subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });

    this._subscriptions = [];

    if ((this, this._menuSubscription)) {
      this._menuSubscription.unsubscribe();
    }

    this._toolbarService.setToolbarConfiguration([]);
  }

  ngAfterViewInit() {
    if (this._lat && this._lon) {
      this.mapview.moveToLonLatZoom(parseFloat(this._lon), parseFloat(this._lat), parseFloat(this._zoom));
    }
  }

  handleMapTableView() {
    this._mapTableSrv
      .getMapTableConfig()
      .pipe(filter(Boolean), takeUntil(this.destroy$))
      .subscribe({
        next: (config) => (this.mapTableConfig = config),
        error: () => (this.mapTableConfig = null)
      });
  }

  public async changeActivePreview($event: FeatureActionEvent) {
    const { indexChange, action, features } = $event;

    const index = indexChange;
    const singleFeature = index >= 0;
    let activeFeature: Feature<Geometry>;
    let modalData;
    let urn;

    //se index è -1 si fa riferimento a N feature, ad oggi l'unico scenario in questa casistica è la action highlight e zoom-fit
    if (singleFeature) {
      activeFeature = features[index];
      modalData = activeFeature['details'] ? activeFeature['details'] : activeFeature['values_'] ? activeFeature['values_'] : null;
      urn = activeFeature.getId().toString();
      modalData.urn = urn;
    }

    switch (action) {
      case 'view':
      case 'viewCustom':
        //pulisco i layer tmp
        this._mapService.clearFeatureFromTemporaryLayerById('highlight_feature_', false, true);
        this._mapService.clearFeatureFromTemporaryLayerById('bounding_box_', false, true);

        if (activeFeature['values_']['detailUrl']) {
          this._featureSelectService
            .getFeatureInfo(activeFeature)
            .then(async (response: any) => {
              activeFeature.setProperties(response.properties);

              // open modal
              this._featureSelectService.mapFeatureToModal(activeFeature, action);
            })
            .catch((err) => {
              const modalContent = this._translateService.instant('common.data_retrieving_error');
              this._ebwModal.openErrorModal({
                content: modalContent,
                title: this._translateService.instant('common.error')
              });
            });
        } else {
          const { config_collection } = this._featureSelectService.getConfigDataFromUrn(
            activeFeature.getId ? activeFeature.getId() : activeFeature['id']
          );
          modalData.config_collection = config_collection;
          this._featureSelectService.doAction(action, modalData);
        }

        break;
      case 'highlight':
        //pulisco i layer tmp
        this._mapService.clearFeatureFromTemporaryLayerById('highlight_feature_', false, true);
        this._mapService.clearFeatureFromTemporaryLayerById('bounding_box_', false, true);

        const features = singleFeature ? [activeFeature] : $event.features;

        features.forEach((feature) => {
          const geojsonFormat = new GeoJSON();
          const geojsonFeature: any = geojsonFormat.writeFeatureObject(feature);

          if (geojsonFeature.geometry) {
            geojsonFeature.geometry.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(geojsonFeature.geometry.coordinates);

            this._mapService.addFeatureToTemporaryLayer(
              geojsonFeature.geometry,
              this._highlightStyle,
              false,
              false,
              // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
              `highlight_feature_${Math.random()}`
            );
          }
        });

        break;

      case 'zoom-fit':
        //pulisco i layer tmp
        this._mapService.clearFeatureFromTemporaryLayerById('highlight_feature_', false, true);
        this._mapService.clearFeatureFromTemporaryLayerById('bounding_box_', false, true);

        $event.features.forEach((feature) => {
          const geojsonFormat = new GeoJSON();
          const geojsonFeature: any = geojsonFormat.writeFeatureObject(feature);

          if (geojsonFeature.geometry) {
            geojsonFeature.geometry.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(geojsonFeature.geometry.coordinates);

            // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
            this._mapService.addFeatureToTemporaryLayer(geojsonFeature.geometry, null, false, false, `bounding_box__${Math.random()}`);
          }
        });
        this._mapService.centerMapOnTempLayer();

        break;

      case 'kmz_circuiti':
        this._telcoService.exportKmz(activeFeature.getId().toString());
        break;
      case 'raccordi':
        //pulisco i layer tmp
        this._mapService.clearFeatureFromTemporaryLayerById('highlight_feature_', false, true);
        this._mapService.clearFeatureFromTemporaryLayerById('bounding_box_', false, true);

        const queryParams = { urn: activeFeature.getId().toString() };
        const sidebarAction = ['fiber-joints'];
        await this._router.navigate(['/home', { outlets: { sidebarAction } }], { queryParams });
        this._splitViewService.destroy();
        break;
      case 'cavo_raccordo':
        //pulisco i layer tmp
        this._mapService.clearFeatureFromTemporaryLayerById('highlight_feature_', false, true);
        this._mapService.clearFeatureFromTemporaryLayerById('bounding_box_', false, true);

        await this.handleCavoRaccordoAction(activeFeature, $event);
        break;
      default: {
        //pulisco i layer tmp
        this._mapService.clearFeatureFromTemporaryLayerById('highlight_feature_', false, true);
        this._mapService.clearFeatureFromTemporaryLayerById('bounding_box_', false, true);

        this._ebwModal.openCustomModal(
          {
            content: '',
            title: 'Info'
          },
          ReportDialogComponent,
          {
            data: {
              styleConfig: REPORT_DIALOG_CUSTOM_STYLE,
              data: Object.keys(modalData).map((key) => ({ key, value: modalData[key] }))
            }
          }
        );
      }
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  async handleCavoRaccordoAction(feature: Feature, event: { indexChange: number; action: string; features: Feature[] }) {
    const data = { urn: feature.getId().toString() };
    // Apro modale di creazione cavo di raccordo
    const ref = this._dialog.open(RaccordiCavoEditComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '70%',
      data
    });

    const success = await lastValueFrom(ref.afterClosed());
    if (!success) {
      return;
    }

    // Se tutto andato bene chiedo all'utente se vuole visualizzare il cavo appena creato
    const title = '';
    const content = 'Vuoi visualizzare il cavo di raccordo appena creato?';
    const modal = this._ebwModal.openConfirmModal({ title, content });
    const confirm = await lastValueFrom(modal.afterClosed());

    if (!confirm) {
      return;
    }

    // Nel caso di risposta affermativa uso processo già esistente per aprire la lista dei raccordi
    this.changeActivePreview({ ...event, action: 'raccordi' });
  }

  // --------------------------------------------------------------------------------------------------------------------

  private _handleInputData() {
    switch (this._mapService.mapInputData.mapAction) {
      case 'fit':
        if (this._mapService.mapInputData.geom) {
          let geom;

          // Circle
          if (!!this._mapService.mapInputData.geom.type && this._mapService.mapInputData.geom.type == 'Circle') {
            geom = new Point(this._mapService.mapInputData.geom.coordinates);
          }
          // Other geometries
          else {
            geom = new GeoJSON().readGeometry(this._mapService.mapInputData.geom, {
              dataProjection: MapService.EPSG_3857_KEY,
              featureProjection: MapService.EPSG_3857_KEY
            });
          }

          // prepare a default size in case the map dimensions are 0
          const size = this._mapService.openLayersMap.getSize();

          if (size[0] === 0) {
            size[0] = 500;
          }
          if (size[1] === 0) {
            size[1] = 500;
          }

          this._mapService.openLayersMap.getView().fit(geom, { size: size });

          const feature = new Feature({ geometry: geom });
          const style = this._layerStylesService.buildStyleFromFeatureStyle(this._drawingService.blueFillFeatureStyle());

          feature.setStyle(style);

          this._mapService.tempLayer.getSource().addFeature(feature);
        }
        break;
    }
  }

  // #region coseDaRivedere
  private _instanceReportComponent(filteredFeature) {
    const reportComponent = this._featureSelectService.modalRef.componentInstance.childComponentRef as ComponentRef<ReportDialogComponent>;
    reportComponent.instance.reportData = {
      data: filteredFeature,
      styleConfig: REPORT_DIALOG_CUSTOM_STYLE
    };
    return reportComponent;
  }

  private _initFeatureModal() {
    this._subscriptions.push(
      this._featureSelectService.customModalSubject.subscribe((response) => {
        let feature = response.feature;

        if (feature && this._featureSelectService.modalRef) {
          const mappedFeaure = this._featureSelectService.parseFeature(feature);

          feature = this._featureSelectService.objectToArray(mappedFeaure);

          const titleIndex = feature.findIndex((item) => item.key === 'title');
          if (titleIndex !== -1) feature.splice(titleIndex, 1);

          this._featureSelectService.modalRef
            .afterOpened()
            .pipe(take(1))
            .subscribe(() => {
              this._instanceReportComponent(feature);
              this._instanceButtonComponent(feature);
            });
        }
      })
    );
  }

  private _setCollectionConfig() {
    const mapConfig = this._configService.getMapConfiguration();
    this._featureSelectService.setCollectionConfig({
      collections: mapConfig.collections,
      enumerators: mapConfig.enumerators
    });
  }

  private _setDatasetConfig(datasetConf) {
    this._featureSelectService.setDatasetConfig(datasetConf);
  }

  private _setTelcoConfig() {
    const { otdr, analisiSchiacciamenti, toolFattibilita } = this._configService.getMapConfiguration();
    this._telcoService.setTelcoConfig({ otdr, analisiSchiacciamenti, toolFattibilita });
  }

  //  #endregion

  private _setMapConfigAndToolbar(mapConf) {
    // Retrieve dynamic map configuration from the sessionStorage
    this.dynamicConfigMappaJSON = mapConf;

    const conf = this.dynamicConfigMappaJSON as IMapConfig;

    []
      .concat(conf.wmsLayers, conf.wmtsLayers, conf.xyzLayers, conf.vectorLayers)
      .filter((layer) => !!layer.auth)
      .forEach((layer) => {
        if (layer.auth.type == 'oauth') {
          this._mapService.setToken(layer.auth.type, layer.id, () => {
            return sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY);
          });
        } else {
          this._mapService.setToken(layer.auth.type, layer.id, layer.auth.key);
        }
      });
    this.mapConfig = this.dynamicConfigMappaJSON as IMapConfig;

    this._setCollectionConfig();
    this._setDatasetConfig(this.mapConfig.datasets);
    this._setTelcoConfig();
    this._setToolbarActions(this.mapConfig);
  }

  private _initSubscriptions() {
    this._subscriptions.push(
      this._route.queryParams.subscribe((params) => {
        this._lat = params.lat;
        this._lon = params.lon;
        this._zoom = params.zoom;
      })
    );

    this._subscriptions.push(
      this._route.data.subscribe((data) => {
        if (data) {
          this._mapService.mapInputData = data;
          this._mapService.showBackButton =
            this._mapService.mapInputData.showBackButton !== undefined ? this._mapService.mapInputData.showBackButton : false;

          this._handleInputData();
        }
      })
    );

    this._subscriptions.push(
      this._mapService.backButtonEventSubject.subscribe((event) => {
        this._navigation.goBack();
      })
    );

    this._subscriptions.push(
      this._featureSelectService.featuresViewerClosedSubject$.subscribe((event) => {
        this._mapService.clearFeatureFromTemporaryLayerById('highlight_feature');
      })
    );
  }

  private async _setToolbarActions(conf: IMapConfig) {
    const toolbarBaseConfig: IToolbarAction[] = [];

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.DRAWING_TOOL)) {
      toolbarBaseConfig.push({
        id: 'drawing-tool',
        label: 'map.drawing_tool.tooltip',
        icon: 'activity_zone',
        eventType: 'custom',
        isActive: conf.mapLayout ? conf.mapLayout.drawingTool.active : true
      });
    }

    // extract all the layers in the "sceltaRappresentazione"
    let baseLayers = [];

    for (let tab of <any[]>this._configService.getMapConfiguration().sceltaRappresentazione) {
      for (let group of tab.content) {
        baseLayers = baseLayers.concat(group.values);
      }
    }

    // check if any of these layers should be shown. stop as soon as you find a layer that should be shown
    let visibilityCheck = false;

    for (let baseLayer of baseLayers) {
      let result = await this._configSidebarService.checkLayerVisibility(baseLayer);

      if (result) {
        visibilityCheck = true;
        break;
      }
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.LAYERS_CONFIGURATOR) && visibilityCheck) {
      toolbarBaseConfig.push({
        id: 'layers',
        label: 'map.map_component.layer_selection',
        icon: 'stacks',
        eventType: 'panel',
        isActive: conf.mapLayout?.layerSelection?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.DYNAMIC_LAYERS)) {
      toolbarBaseConfig.push({
        id: 'dynamic-layers',
        label: 'map.map_component.additional_layers',
        icon: 'stacks',
        eventType: 'panel',
        fill: true,
        isActive: conf.mapLayout?.dynamic_layers?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.SHAPE_CONFIGURATOR)) {
      toolbarBaseConfig.push({
        id: 'shape-layers',
        label: 'map.sidebar.shape.tooltip',
        icon: 'space_dashboard',
        eventType: 'panel',
        isActive: conf.mapLayout?.shape_layers?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.BOOKMARKS)) {
      toolbarBaseConfig.push({
        id: 'bookmarks',
        label: 'map.sidebar.bookmark.my_views',
        icon: 'bookmarks',
        eventType: 'panel',
        isActive: conf.mapLayout?.bookmark?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.QUERY)) {
      toolbarBaseConfig.push({
        id: 'query',
        label: 'map.sidebar.query.my_query',
        icon: 'build',
        eventType: 'panel',
        isActive: conf.mapLayout?.query?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.VALVE)) {
      toolbarBaseConfig.push({
        id: 'valves',
        label: 'map.sidebar.valve.my_valve',
        icon: 'rebase_edit',
        eventType: 'panel',
        isActive: conf.mapLayout?.valves?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.OTDR)) {
      toolbarBaseConfig.push({
        id: 'otdr',
        label: 'map.sidebar.otdr.title',
        icon: 'usb_off',
        eventType: 'panel',
        isActive: conf.mapLayout?.otdr?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.PSA)) {
      toolbarBaseConfig.push({
        id: 'psa',
        label: 'map.sidebar.psa.title',
        icon: 'water_drop',
        eventType: 'panel',
        fill: true,
        isActive: conf.mapLayout?.psa?.active ?? false
      });

      const getIconPath = (iconName: string) => this._domSanitizer.bypassSecurityTrustResourceUrl(`./assets/psa/${iconName}.svg`);

      const icons = ['probabilita', 'efficacia', 'gravita', 'pericolo_biologico', 'pericolo_chimico', 'pericolo_fisico', 'pericolo_quantitativo'];
      icons.forEach((i) => this.matIconRegistry.addSvgIcon(i, getIconPath(i)));
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.CONTROLLI)) {
      toolbarBaseConfig.push({
        id: 'controlli',
        label: 'map.sidebar.controlli.title',
        icon: 'labs',
        eventType: 'panel',
        fill: true,
        isActive: conf.mapLayout?.controlli?.active ?? false,
        wide: true
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.TRASMISSIONE_ANTEA)) {
      toolbarBaseConfig.push({
        id: 'trasmissione-antea',
        label: 'map.sidebar.trasmissione_antea.title',
        icon: 'cloud_upload',
        eventType: 'panel',
        isActive: conf.mapLayout?.trasmissioneAntea?.active ?? false,
        wide: true
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.ANALISI_SCHIACCIAMENTI)) {
      toolbarBaseConfig.push({
        id: 'analisi-schiacciamenti',
        label: 'map.sidebar.analisi_schiacciamenti.title',
        icon: 'hardware',
        eventType: 'panel',
        isActive: conf.mapLayout?.analisiSchiacciamenti?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.CHECKINOUT)) {
      toolbarBaseConfig.push({
        id: 'checkin-out',
        label: 'map.sidebar.checkInOut.title',
        icon: 'work_outline',
        eventType: 'panel',
        isActive: conf.mapLayout?.checkInOut?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.TRACING)) {
      toolbarBaseConfig.push({
        id: 'tracing',
        label: 'map.sidebar.tracing.title',
        icon: 'flowchart',
        eventType: 'panel',
        isActive: conf.mapLayout?.tracing?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.CHECKIN_ANALYSIS)) {
      toolbarBaseConfig.push({
        id: 'checkin-analysis',
        label: 'map.sidebar.checkin_analysis.title',
        icon: 'playlist_add_check',
        eventType: 'panel',
        isActive: conf.mapLayout?.checkInAnalysis?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.FIBER_JOINTS)) {
      toolbarBaseConfig.push({
        id: 'fiber-joints',
        label: 'map.sidebar.raccordo-cavo.title',
        icon: 'cable',
        eventType: 'panel',
        isActive: conf.mapLayout?.fiberJoints?.active ?? false
      });
    }

    if (this._permissionService.checkPermission(PERMISSION_NAME.MAP.SIDEBAR.TOOL_FATTIBILITA)) {
      toolbarBaseConfig.push({
        id: 'fattibilita',
        label: 'map.sidebar.fattibilita.title',
        icon: 'route',
        eventType: 'panel',
        isActive: conf.mapLayout?.toolFattibilita?.active ?? false
      });
    }

    this._toolbarService.setToolbarConfiguration(toolbarBaseConfig.filter((_) => _.isActive));
  }

  private _instanceButtonComponent(fields: { key: string; value: string }[]) {
    try {
      const assetsAllowedForKmz = ['fw_link', 'fw_circuit'];

      const urnField = fields.find((f) => f.key === 'urn');

      if (!urnField) {
        return;
      }

      const urn = urnField.value;

      if (!urn) {
        return;
      }
      const isKmzAllowed = assetsAllowedForKmz.some((asset) => urn.includes(asset));
      if (!isKmzAllowed) {
        return;
      }
      const customButton = this._featureSelectService.modalRef.componentInstance.actionComponentRef as ComponentRef<CustomButtonComponent>;
      customButton.instance.buttons = ['Download .kmz'];
      customButton.instance.styleButton = 'mat-button-base';
      customButton.instance.changeComponentHandler.subscribe((value) => this.handleModalActions(value, urn));
      return customButton;
    } catch (error) {
      console.error('[MapWrapper][_instanceButtonComponent]', error);
    }
  }

  // ----------------------------------------------------------------------------------------------------------------------------------

  handleModalActions(buttonName: string, urn: string) {
    switch (buttonName) {
      case 'Download .kmz':
        this._telcoService.exportKmz(urn);
        break;
      default:
        console.warn('[MapWrapper][handleModalActions] Azione non gestita: ', buttonName);
        break;
    }
  }
}
